import { Avatar, Backdrop, Box, Button, Card, Checkbox, Divider, FormControl, FormControlLabel, Grid, Hidden, IconButton, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import LOGO from '../../assets/images/logo_v3.svg';
import { numberFormat } from "../../utils/nunberFormat";

import CancelIcon from '@mui/icons-material/Cancel';
import useOrderDetailController from "../order/controller/orderDetailControllerOld";
import { EN_ACCEPT_LANGUAGE } from "../../constants/acceptLanguageApi";
import { concatenateStrings } from "../../utils/concatenateStrings";
import { convertOrderStatusCodeToOrderStatusEnum, convertOrderStatusEnumToStatusCode } from "../../utils/convertOrderStatusValue";
import { convertDateStringToNormalString, convertDateStringToNormalTime } from "../../utils/convertDateFormat";
import { OrderStatusEnum } from "../../enums/orderStatus";
import { OrderModel } from "../../models/order";
import { calculateOrderProductUnitByCarts } from "../../utils/calculateOrderProductUnitByCarts";
import { CartsModel } from "../../models/carts";
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { SECONDARY_COLOR, TEXT_TITLE_COLOR } from "../../constants/colors";
import RiderScanToConfirm from "./RiderScanToConfirm";
import ConfirmDialog from "../../components/ConfirmDialog";
import useOrderReturnDetailController from "./controller/orderReturnDetailController";
import { IMAGE_RIDER_ROOT_URL } from "../../constants/imageUrl";
import { viewGoogleMapLocation } from "../../utils/viewGoogleMapLocation";
import PromotionText from "../../components/PromotionText";
import { PromotionTypeEnum } from "../../enums/promotionType";

interface Props {
    onClose: VoidFunction,
    selectedData: OrderModel,
    onRefetchData: () => Promise<void>;
}

const OrderDetail = ({ onClose, selectedData, onRefetchData }: Props) => {
    const { i18n, t } = useTranslation("global");
    const controller = useOrderReturnDetailController({
        onClose,
        selectedData,
        onRefetchData
    });

    return <Box>
        <IconButton onClick={onClose} sx={{ mb: 2 }}>
            <KeyboardBackspaceIcon />
        </IconButton>
        <Typography variant="h5" color="#737373" fontWeight="bold" sx={{
            mb: 3,
            mt: 2
        }}>{selectedData?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.To_Ship) ? "Rider scan to confirm" : t("order_page.Order Details")}</Typography>
        {
            selectedData?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.To_Ship)
            &&
            <Box mb={2}>
                <RiderScanToConfirm selectedData={selectedData} onRefetchData={onRefetchData} onClose={onClose} />
            </Box>
        }


        <Card sx={{ boxShadow: '0px 0px 2px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12);', p: 3 }}>
            <Hidden smUp>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, width: '100%', justifyContent: 'space-between', flexDirection: 'column' }}>
                    <img src={LOGO} alt="" style={{ width: 104, height: 32 }} />
                    <Typography fontSize={32}>{t("order_page.Order number")}: {selectedData?.orderNumber}</Typography>
                    <Typography fontSize={32} fontWeight="bold">{t("order_page.Order ID")}: {selectedData?.orderNo}</Typography>
                </Box>
            </Hidden>
            <Hidden smDown>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, width: '100%', justifyContent: 'space-between' }}>
                    <img src={LOGO} alt="" style={{ width: 104, height: 32 }} />
                    <Typography fontSize={32}>{t("order_page.Order number")}: {selectedData?.orderNumber}</Typography>
                    <Typography fontSize={32} fontWeight="bold">{t("order_page.Order ID")}: {selectedData?.orderNo}</Typography>
                </Box>
            </Hidden>
            {
                selectedData?.updateByAdmin
                &&
                <Typography><span style={{
                    lineHeight: 3,
                    backgroundColor: "red",
                    borderRadius: "6px",
                    padding: "2px 8px",
                    color: 'white'
                }}>ໄດ້ສິນຄ້າບໍ່ຄົບ</span></Typography>
            }
            <Typography>{t("order_page.Status")} : <span style={{
                lineHeight: 3,
                backgroundColor: 'rgba(255, 86, 48, 0.48)',
                borderRadius: "6px",
                padding: "2px 8px"
            }}>{convertOrderStatusCodeToOrderStatusEnum(selectedData?.status?.code)}</span></Typography>
            <Typography>{t("order_page.Payment method")} : <span style={{
                lineHeight: 3,
                backgroundColor: '#FFF2D6',
                borderRadius: "6px",
                padding: "2px 8px",
                color: "#1B806A",
                display: selectedData?.paymentMethod ? "inline" : "none"
            }}>{selectedData?.paymentMethod}</span></Typography>
            <Typography>{t("order_page.Shipment method")} : <span style={{
                lineHeight: 3,
                backgroundColor: SECONDARY_COLOR,
                borderRadius: "6px",
                padding: "2px 8px",
                color: "white",
                display: selectedData?.orderMethod ? "inline" : "none"
            }}>{selectedData?.orderMethod}</span></Typography>
            <Typography>ໄລຍະທາງ: <span style={{ fontWeight: 'bold' }}>{selectedData?.distance} Km</span></Typography>
            <Typography>ຂໍ້ມູນແຜນທີ່: <span style={{ fontWeight: 'bold' }}>ຮ້ານຄ້າ: latitude: {selectedData?.merchant?.location.coordinates[1]} longitude: {selectedData?.merchant?.location.coordinates[0]} <span style={{ color: 'red' }}>ລູກຄ້າ</span>: latitude: {selectedData?.address?.latitude}, longitude: {selectedData?.address?.longitude}</span></Typography>
            {
                selectedData?.remarkFromRider
                &&
                <Typography>ເຫດຜົນໃນການສົ່ງຄືນ: <span style={{ fontWeight: 'bold', color: 'red' }}>{selectedData?.remarkFromRider}</span></Typography>

            }
            <Button onClick={() => viewGoogleMapLocation(selectedData?.merchant?.location.coordinates[1], selectedData?.merchant?.location.coordinates[0], parseFloat(selectedData?.address?.latitude), parseFloat(selectedData?.address?.longitude))} color="secondary" variant="contained" sx={{ color: 'white', textTransform: 'none', mb: 2, mt: 1 }}>ໄປຍັງສະຖານທີ່</Button>
            <Box mt={5}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Typography color="#919EAB" fontWeight="bold">{t("order_page.From")}</Typography>
                        <Box mt={2}>
                            <Typography>{i18n.language === EN_ACCEPT_LANGUAGE ? selectedData?.merchant?.nameEn : selectedData?.merchant?.nameLa}</Typography>
                            <Typography>{selectedData?.merchant?.village && selectedData?.merchant?.village} {selectedData?.merchant?.district && ', ' + selectedData?.merchant?.district} {selectedData?.merchant?.provice && ', ' + selectedData?.merchant?.provice}</Typography>
                            <Typography>{t("order_page.Phone")} : {selectedData?.merchant?.phone}</Typography>
                        </Box>

                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography color="#919EAB" fontWeight="bold">{t("order_page.Customer")}</Typography>
                        <Box mt={2}>
                            <Typography>{selectedData?.customer?.profile?.fullName}</Typography>
                            <Typography>{selectedData?.customer?.profile?.address}</Typography>
                            <Typography>{t("order_page.Phone")} : {selectedData?.customer?.phone}</Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <Box mt={5}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Typography color="#919EAB" fontWeight="bold">{t("order_page.Order Date")}</Typography>
                        <Box mt={2}>
                            <Typography>{convertDateStringToNormalString(selectedData?.createdAt)}</Typography>
                        </Box>

                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography color="#919EAB" fontWeight="bold">ເວລາທີ່ລູກຄ້າຕ້ອງໄດ້ເຄື່ອງ</Typography>
                        <Typography color="#616161">{selectedData?.deliveryTime && convertDateStringToNormalString(selectedData?.deliveryTime)}</Typography>
                    </Grid>
                </Grid>
            </Box>
            <Box mt={10}>
                <TableContainer >
                    <Table>
                        <TableBody>
                            <TableRow hover tabIndex={-1} role="checkbox">
                                <TableCell align="left" sx={{ color: '#637381' }}>#</TableCell>
                                <TableCell align="left" sx={{ color: '#637381' }}>{t("Description")}</TableCell>
                                <TableCell align="left" sx={{ color: '#637381' }}>{t("Barcode")}</TableCell>
                                <TableCell align="left" sx={{ color: '#637381' }}>{t("order_page.Qty")}</TableCell>
                                <TableCell align="left" sx={{ color: '#637381' }}>{t("order_page.Unit Price")}</TableCell>
                                <TableCell align="left" sx={{ color: '#637381' }}>{t("product_merchant_page.Discount")}</TableCell>
                                <TableCell align="left" sx={{ color: '#637381' }}>{t("product_merchant_page.Promotion")}</TableCell>
                                <TableCell align="left" sx={{ color: '#637381' }}>{t("order_page.Total")}</TableCell>
                            </TableRow>
                            {
                                selectedData?.carts?.map((item: CartsModel, index: number) => {
                                    return <TableRow key={index} hover tabIndex={-1} role="checkbox">
                                        <TableCell align="left" >{numberFormat(index + 1)}</TableCell>
                                        <TableCell align="left" >{i18n.language === EN_ACCEPT_LANGUAGE ? item?.product?.price?.masterProduct?.productNameEn : item?.product?.price?.masterProduct?.productNameLa}</TableCell>
                                        <TableCell align="left" >{concatenateStrings(item?.product?.price?.masterProduct?.barcodes)}</TableCell>
                                        <TableCell align="left" >{numberFormat(item?.quantity)}</TableCell>
                                        <TableCell align="left" >{numberFormat(item?.productPrice)} {t("order_page.kip")}</TableCell>
                                        <TableCell align="left" >{numberFormat(item?.discount)} {t("order_page.kip")}</TableCell>
                                        <TableCell align="left" >{t(`order_page.${item?.product?.promotion?.promotionType}`)}</TableCell>
                                        <TableCell align="left" >{numberFormat(item?.total)} {t("order_page.kip")}</TableCell>
                                    </TableRow>
                                })
                            }

                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            {/* {
                controller?.dataOrderLog
                &&
                <Box mt={10}>
                    <Typography variant="h5" color="red" textAlign="center" sx={{ mb: 1 }}>ລາຍການກ່ອນມີການແກ້ໄຂ</Typography>
                    <TableContainer >
                        <Table>
                            <TableBody>
                                <TableRow hover tabIndex={-1} role="checkbox">
                                    <TableCell align="left" sx={{ color: '#637381' }}>#</TableCell>
                                    <TableCell align="left" sx={{ color: '#637381' }}>Description</TableCell>
                                    <TableCell align="left" sx={{ color: '#637381' }}>Barcodes</TableCell>
                                    <TableCell align="left" sx={{ color: '#637381' }}>Qty</TableCell>
                                    <TableCell align="left" sx={{ color: '#637381' }}>Unit Price</TableCell>
                                    <TableCell align="left" sx={{ color: '#637381' }}>Discount</TableCell>
                                    <TableCell align="left" sx={{ color: '#637381' }}>Promotion</TableCell>
                                    <TableCell align="left" sx={{ color: '#637381' }}>Total</TableCell>
                                </TableRow>
                                {
                                    controller.dataOrderLog?.carts?.map((item: CartsModel, index: number) => {
                                        return <TableRow key={index} hover tabIndex={-1} role="checkbox">
                                            <TableCell align="left" >{numberFormat(index + 1)}</TableCell>
                                            <TableCell align="left" >{i18n.language === EN_ACCEPT_LANGUAGE ? item?.product?.price?.masterProduct?.productNameEn : item?.product?.price?.masterProduct?.productNameLa}</TableCell>
                                            <TableCell align="left" >{concatenateStrings(item?.product?.price?.masterProduct?.barcodes)}</TableCell>
                                            <TableCell align="left" >{numberFormat(item?.quantity)}</TableCell>
                                            <TableCell align="left" >{numberFormat(item?.productPrice)} kip</TableCell>
                                            <TableCell align="left" >{numberFormat(item?.discount)} kip</TableCell>
                                            <TableCell align="left" ><PromotionText promotionType={item?.product?.promotion?.promotionType as PromotionTypeEnum} /></TableCell>
                                            <TableCell align="left" >{numberFormat(item?.total)} kip</TableCell>
                                        </TableRow>
                                    })
                                }

                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            } */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'end', gap: 5 }}>
                <Box>
                    <Typography textAlign="end">{t("order_page.Total unit")}</Typography>
                    <Typography textAlign="end">{t("order_page.Subtotal")}</Typography>
                    <Typography textAlign="end">ສ່ວນຫຼຸດຈາກໂປຣໂມຊັນ</Typography>
                    <Typography textAlign="end">ສ່ວນຫຼຸດຈາກຄູປອງ</Typography>
                    <Typography textAlign="end">{t("order_page.Taxes")}</Typography>
                    <Typography textAlign="end">{t("order_page.Delivery fee")}</Typography>
                    <Typography textAlign="end" fontWeight="bold">{t("order_page.Total")}</Typography>
                </Box>
                <Box>
                    <Typography textAlign="end">{numberFormat(calculateOrderProductUnitByCarts(selectedData?.carts))} units</Typography>
                    <Typography textAlign="end">{numberFormat(selectedData?.totalNomalPrice)} {t("order_page.kip")}</Typography>
                    <Typography textAlign="end" color="red">- {numberFormat(selectedData?.totalDiscountPriceForPromotion)} {t("order_page.kip")}</Typography>
                    <Typography textAlign="end" color="red">- {numberFormat(selectedData?.totalDiscountPriceForCoupon)} {t("order_page.kip")}</Typography>
                    <Typography textAlign="end">{numberFormat(selectedData?.totalVatPrice)} {t("order_page.kip")}</Typography>
                    <Typography textAlign="end">{numberFormat(selectedData?.deliveryFee)} {t("order_page.kip")}</Typography>
                    <Typography textAlign="end" fontWeight="bold">{numberFormat(selectedData?.totoalWithDeliveryFee)} {t("order_page.kip")}</Typography>
                </Box>

            </Box>
            <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
                <Box sx={{ backgroundColor: '#C8C8C8;', height: "1px" }} />
            </Box>
            <Hidden smDown>
                <Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex', gap: 2, alignItems: "center" }}>
                        <Box>
                            <Typography fontWeight='bold' color={TEXT_TITLE_COLOR}>{t("order_page.Driver Name")} : <span style={{ color: 'red' }}>{selectedData?.rider?.profile?.fullName}</span> </Typography>
                            <Typography fontWeight='bold' color={TEXT_TITLE_COLOR}>{t("order_page.Driver contact")} : <span style={{ color: 'red' }}> {selectedData?.rider?.profile?.phoneNumber}</span></Typography>
                        </Box>
                        <Avatar
                            alt="Rider"
                            src={IMAGE_RIDER_ROOT_URL + selectedData?.rider?.profile?.image?.url}
                            sx={{
                                width: "70px",
                                height: '70px'
                            }}
                        />

                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'end' }}>
                        <Box>
                            <Typography fontWeight='bold' color={TEXT_TITLE_COLOR}>Contact admin for support :</Typography>
                            <Typography fontWeight='bold' color={TEXT_TITLE_COLOR}>020 554562236</Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Hidden>
            <Hidden smUp>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, width: '100%', justifyContent: 'space-between', flexDirection: 'column' }}>
                    <Box>
                        <Typography fontWeight='bold' color={TEXT_TITLE_COLOR}>{t("order_page.Driver Name")} : <span style={{ color: 'red' }}>{selectedData?.rider?.profile?.fullName}</span> </Typography>
                        <Typography fontWeight='bold' color={TEXT_TITLE_COLOR}>{t("order_page.Driver contact")} : <span style={{ color: 'red' }}> {selectedData?.rider?.profile?.phoneNumber}</span></Typography>
                    </Box>
                    <Avatar
                        alt="Rider"
                        src={IMAGE_RIDER_ROOT_URL + selectedData?.rider?.profile?.image?.url}
                        sx={{
                            width: "70px",
                            height: '70px'
                        }}
                    />
                </Box>
                <Box sx={{ textAlign: 'center ' }}>
                    <Typography fontWeight='bold' color={TEXT_TITLE_COLOR}>Contact admin for support :</Typography>
                    <Typography fontWeight='bold' color={TEXT_TITLE_COLOR}>020 554562236</Typography>
                </Box>
            </Hidden>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'end' }}>
                {/* <TextField inputRef={controller.descriptionRef} multiline minRows={4} fullWidth sx={{ maxWidth: 350 }} placeholder="ຄຳອະທິບາຍ" /> */}
            </Box>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'end' }}>
                <Button variant="contained" color="secondary" sx={{ textTransform: 'none', width: "100px", color: 'white' }} onClick={controller.handleOpenConfirmDialog}>{t("Return")}</Button>
            </Box>
        </Card>

        <ConfirmDialog
            open={controller.openConfirmDialog}
            title={`Do you want to return order?`}
            loading={controller.loadingConfirmDialog}
            onCancel={controller.handleCloseConfirmDialog}
            onConfirm={controller.handleReturnOrder}
        />

    </Box>
}
export default OrderDetail;